import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react"
import {
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
} from "@mui/material"

import FolderIcon from "@mui/icons-material/Folder"
import ReplayIcon from "@mui/icons-material/Replay"
import { ReactComponent as DeleteOutlineIcon } from "assets/images/icons/trash.svg"
import MDBox from "components/MDBox"

import { ReactComponent as ImageIcon } from "assets/images/icons/img.svg"
import { ReactComponent as PdfIcon } from "assets/images/icons/pdf.svg"

const MAX_FILES = 5
const MAX_SIZE_MB = 20

const getFileIcon = (fileName = "") => {
  const ext = fileName.split(".").pop().toLowerCase()
  if (ext === "pdf") return <PdfIcon sx={{ color: "#E53935" }} />
  if (["jpg", "jpeg", "png", "gif"].includes(ext))
    return <ImageIcon sx={{ color: "#5E72E4" }} />
  return <ImageIcon sx={{ color: "#5E72E4" }} />
}

const formatKB = (bytes = 0) => (bytes / 1024).toFixed(2) + " KB"

// ✅ convert backend file -> item (completed)
const mapRemoteToItem = (f) => {
  const name = f.fileName || f.name || f.originalName || "unknown"
  const size = Number(f.sizeBytes ?? f.size ?? 0)
  return {
    // dùng id/url/name để làm key/identity
    key: String(f.id ?? f.attachmentId ?? f.url ?? name),
    isRemote: true,
    remote: f, // giữ nguyên object backend nếu bạn cần
    file: null, // remote không có File object
    name,
    size,
    url: f.url,
    status: "completed",
    progress: 100,
    message: "Completed",
  }
}

// ✅ convert local File -> item (uploading)
const mapLocalToItem = (file) => {
  const sizeMB = file.size / (1024 * 1024)
  if (sizeMB > MAX_SIZE_MB) {
    return {
      key: `${file.name}-${file.size}-${file.lastModified}`,
      isRemote: false,
      file,
      name: file.name,
      size: file.size,
      url: "",
      status: "error",
      progress: 0,
      message: "File loading error",
    }
  }
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    isRemote: false,
    file,
    name: file.name,
    size: file.size,
    url: "",
    status: "uploading",
    progress: 30,
    message: "Uploading...",
  }
}

const AttachFileUpload = forwardRef(function AttachFileUpload(
  { onChange, selectedFiles = [], required = true, errorMessage = "" },
  ref
) {
  const inputRef = useRef()
  const [files, setFiles] = useState([]) // items: {isRemote, file?, name, size, url, status, progress, message}

  // ✅ helper: trả về list để submit
  // - remote: trả về object backend (remote.remote)
  // - local: trả về File
  const getSubmitValue = (list) =>
    list
      .filter((x) => x.status !== "error")
      .map((x) => (x.isRemote ? x.remote : x.file))

  // ✅ Expose API cho parent
  useImperativeHandle(ref, () => ({
    getValue: () => getSubmitValue(files),
    getItems: () => files,
    clear: () => {
      setFiles([])
      onChange?.([])
      if (inputRef.current) inputRef.current.value = ""
    },
  }))

  // ✅ HYDRATE: khi backend trả selectedFiles -> hiển thị sẵn
  useEffect(() => {
    const remoteItems = (selectedFiles || []).map(mapRemoteToItem)

    setFiles((prev) => {
      // merge: giữ local items đang có, + remote mới, tránh trùng key
      const prevMap = new Map(prev.map((x) => [x.key, x]))
      for (const r of remoteItems) {
        prevMap.set(r.key, r)
      }
      const merged = Array.from(prevMap.values()).slice(0, MAX_FILES)
      onChange?.(getSubmitValue(merged))
      return merged
    })
    // eslint-disable-next-line
  }, [JSON.stringify(selectedFiles)])

  const handleAddFiles = (selected) => {
    const selectedArr = Array.from(selected)

    const remaining = MAX_FILES - files.length
    const picked = remaining > 0 ? selectedArr.slice(0, remaining) : []
    if (picked.length === 0) return

    const mapped = picked.map(mapLocalToItem)

    setFiles((prev) => {
      const next = [...prev, ...mapped]
      onChange?.(getSubmitValue(next))
      return next
    })

    // Fake upload simulation
    mapped.forEach((item, idx) => simulateUpload(item, files.length + idx))
  }

  const simulateUpload = (item, index) => {
    if (item.status === "error") return

    let progress = 30
    const interval = setInterval(() => {
      progress += 20
      setFiles((prev) => {
        const next = prev.map((f, i) =>
          i === index
            ? {
                ...f,
                progress: progress >= 100 ? 100 : progress,
                status: progress >= 100 ? "completed" : "uploading",
                message: progress >= 100 ? "Completed" : "Uploading...",
              }
            : f
        )
        onChange?.(getSubmitValue(next))
        return next
      })

      if (progress >= 100) clearInterval(interval)
    }, 600)
  }

  const handleRemove = (idx) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx)
      onChange?.(getSubmitValue(next))
      return next
    })
  }

  const handleRetry = (idx) => {
    setFiles((prev) => {
      const next = prev.map((f, i) =>
        i === idx
          ? { ...f, status: "uploading", progress: 30, message: "Uploading..." }
          : f
      )
      onChange?.(getSubmitValue(next))
      return next
    })
    simulateUpload({ status: "uploading" }, idx)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    handleAddFiles(e.dataTransfer.files)
  }

  return (
    <Box>
      <Typography fontWeight="600" variant="subtitle2" mb={1}>
        Attach File{" "}
        {required && <sup style={{ color: "red" }}>*</sup>}{" "}
        <span style={{ fontWeight: 400, color: "#8898AA" }}>
          (Max 05 files, up to 20MB)
        </span>
      </Typography>
      {errorMessage && (
        <Typography fontSize="12px" color="#F5365C" mb={1}>
          {errorMessage}
        </Typography>
      )}

      <Box
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          background: "#fff",
        }}
        onClick={() => inputRef.current?.click()}
      >
        <Button
          variant="outlined"
          component="label"
          className="modal-leave-application-form-button-upload"
        >
          <FolderIcon />
          Drop files here or Select files
        </Button>

        <input
          type="file"
          hidden
          multiple
          ref={inputRef}
          onChange={(e) => handleAddFiles(e.target.files)}
        />
      </Box>

      {files.length > 0 && (
        <Box mt={2} display="flex" flexDirection="column" gap={1}>
          {files.map((item, idx) => {
            const isCompleted = item.status === "completed"
            const isUploading = item.status === "uploading"
            const isError = item.status === "error"

            return (
              <Box
                key={item.key}
                sx={{
                  border: `1px solid ${isError ? "#EF3948" : "#5E72E4"} `,
                  borderRadius: "10px",
                  p: 1.2,
                  background: isError ? "#FFE5E5" : "#fff",
                }}
              >
                <Box display="flex" gap={2} alignItems="center">
                  <Box>{getFileIcon(item.name)}</Box>

                  <Box display="flex" flexDirection={"column"}>
                    {/* ✅ remote file click to open */}
                    <Typography
                      fontSize="14px"
                      sx={{
                        cursor:
                          item.isRemote && item.url ? "pointer" : "default",
                        textDecoration:
                          item.isRemote && item.url ? "underline" : "none",
                      }}
                      onClick={() => {
                        if (item.isRemote && item.url)
                          window.open(item.url, "_blank")
                      }}
                    >
                      {item.name}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography fontSize="12px" color="#8898AA">
                        {formatKB(item.size)}
                      </Typography>

                      {isCompleted && (
                        <Typography fontSize="12px" color="#2DCE89">
                          Completed
                        </Typography>
                      )}

                      {isUploading && (
                        <Typography fontSize="12px" color="#5E72E4">
                          <MDBox
                            sx={{
                              display: "inline-flex",
                              verticalAlign: "middle",
                              animation: "spin 1s linear infinite",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          >
                            <span />
                          </MDBox>{" "}
                          Uploading...
                        </Typography>
                      )}

                      {isError && (
                        <Typography fontSize="12px" color="#F5365C">
                          File loading error
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} ml={"auto"}>
                    {isError && (
                      <IconButton size="small" onClick={() => handleRetry(idx)}>
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => handleRemove(idx)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {(isUploading || isCompleted) && (
                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{
                        height: 6,
                        borderRadius: 20,
                        backgroundColor: "#EBF2FB",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#5E72E4",
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
})

export default AttachFileUpload
